import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    Sparkles,
    Bot,
    Zap,
    Shield,
    Mic,
    Settings2,
    Heart,
    ChevronLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';

interface VoiceAISettingsProps {
    onClose: () => void;
    isDarkMode: boolean;
}

export default function VoiceAISettings({ onClose, isDarkMode }: VoiceAISettingsProps) {
    const [activePersonality, setActivePersonality] = useState('field_hand');
    const [voiceSpeed, setVoiceSpeed] = useState([1.0]);
    const [voicePitch, setVoicePitch] = useState([1.0]);
    const [wakeWordEnabled, setWakeWordEnabled] = useState(true);

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { type: "spring", stiffness: 300, damping: 24 } as const
        }
    };

    return (
        <div className={`fixed inset-0 z-[60] overflow-y-auto min-h-screen ${isDarkMode ? 'bg-[#020617]' : 'bg-[#F9FAFB]'}`}>

            {/* Header */}
            <div className={`sticky top-0 z-20 border-b ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
                    <Button
                        onClick={onClose}
                        variant="ghost"
                        size="icon"
                        className={`rounded-full ${isDarkMode ? 'hover:bg-slate-800 text-white' : 'hover:bg-gray-100 text-gray-900'}`}
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </Button>
                    <div>
                        <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Voice & AI Command Center
                        </h1>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Configure your intelligent field assistant
                        </p>
                    </div>
                </div>
            </div>

            <motion.div
                className="max-w-4xl mx-auto p-4 pb-40 space-y-8"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >

                {/* Hero Section */}
                <motion.div variants={itemVariants} className="relative overflow-hidden rounded-3xl group">
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 opacity-90" />
                    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />

                    <div className="relative p-8 md:p-12 text-center text-white">
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.2, type: "spring" } as const}
                            className="w-24 h-24 mx-auto mb-6 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 shadow-2xl"
                        >
                            <Sparkles className="w-12 h-12 text-pink-300 drop-shadow-lg" />
                        </motion.div>

                        <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
                            X-Assist Co-Pilot
                        </h2>
                        <p className="text-lg text-purple-100 max-w-2xl mx-auto leading-relaxed">
                            Your intelligent companion for the oilfield. Capable of analyzing data, predicting failures, and keeping you safe.
                        </p>
                    </div>
                </motion.div>

                {/* Interaction Style (Personality) */}
                <motion.div variants={itemVariants}>
                    <div className="flex items-center gap-2 mb-4 px-1">
                        <Bot className={`w-5 h-5 ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`} />
                        <h3 className={`font-bold tracking-wide uppercase text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Interaction Style
                        </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[
                            {
                                id: 'standard',
                                label: 'Standard',
                                desc: 'Concise, efficient, neutral tone.',
                                icon: Bot,
                                color: 'gray'
                            },
                            {
                                id: 'field_hand',
                                label: 'The Field Hand',
                                desc: 'Casual, uses slang ("10-4"), friendly.',
                                icon: Zap,
                                color: 'blue'
                            },
                            {
                                id: 'safety',
                                label: 'Safety Officer',
                                desc: 'Strict, formal, protocol-focused.',
                                icon: Shield,
                                color: 'red'
                            }
                        ].map((persona) => (
                            <motion.button
                                key={persona.id}
                                onClick={() => setActivePersonality(persona.id)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className={`
                  relative overflow-hidden rounded-2xl p-[2px] transition-all text-left h-full
                  ${activePersonality === persona.id
                                        ? `bg-gradient-to-br from-${persona.color}-500 to-${persona.color}-400 shadow-xl shadow-${persona.color}-500/20`
                                        : isDarkMode ? 'bg-slate-800' : 'bg-gray-200'}
                `}
                            >
                                <div className={`
                  rounded-[0.9rem] p-5 h-full relative overflow-hidden
                  ${activePersonality === persona.id
                                        ? isDarkMode ? 'bg-slate-900' : 'bg-white'
                                        : isDarkMode ? 'bg-slate-900' : 'bg-white'}
                `}>
                                    {/* Active Indicator Background */}
                                    {activePersonality === persona.id && (
                                        <div className={`absolute inset-0 opacity-[0.08] bg-${persona.color}-500`} />
                                    )}

                                    <div className="relative z-10 flex flex-col h-full">
                                        <div className="flex justify-between items-start mb-4">
                                            <div className={`
                        w-12 h-12 rounded-xl flex items-center justify-center
                        ${activePersonality === persona.id
                                                    ? `bg-${persona.color}-100 dark:bg-${persona.color}-900/30 text-${persona.color}-600 dark:text-${persona.color}-400`
                                                    : isDarkMode ? 'bg-slate-800 text-gray-500' : 'bg-gray-100 text-gray-500'}
                      `}>
                                                <persona.icon className="w-6 h-6" />
                                            </div>
                                            {activePersonality === persona.id && (
                                                <div className={`
                          px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider
                          bg-${persona.color}-100 dark:bg-${persona.color}-900/40 text-${persona.color}-700 dark:text-${persona.color}-300
                        `}>
                                                    Active
                                                </div>
                                            )}
                                        </div>

                                        <h4 className={`font-bold text-lg mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                            {persona.label}
                                        </h4>
                                        <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {persona.desc}
                                        </p>
                                    </div>
                                </div>
                            </motion.button>
                        ))}
                    </div>
                </motion.div>

                {/* Feature: The Co-Pilot Protocol (Loved One's Voice) */}
                <motion.div variants={itemVariants} className="pt-4">
                    <div className="relative overflow-hidden rounded-3xl p-[1px] bg-gradient-to-r from-fuchsia-600 via-pink-600 to-purple-600 shadow-2xl shadow-fuchsia-500/20">
                        {/* Glossy Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />

                        <div className={`relative ${isDarkMode ? 'bg-slate-900' : 'bg-white'} rounded-[1.4rem] p-8 overflow-hidden`}>
                            {/* Background Art */}
                            <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-fuchsia-500/10 rounded-full blur-[100px] -mr-32 -mt-32 pointer-events-none" />

                            <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center">
                                <div className="flex-shrink-0">
                                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-fuchsia-100 to-purple-100 dark:from-fuchsia-900/30 dark:to-purple-900/30 flex items-center justify-center border-4 border-fuchsia-100 dark:border-fuchsia-900/20 shadow-inner">
                                        <Heart className="w-10 h-10 text-fuchsia-500 fill-fuchsia-500 animate-pulse" />
                                    </div>
                                </div>

                                <div className="flex-1 text-center md:text-left">
                                    <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                                        <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                            Loved One's Voice
                                        </h3>
                                        <span className="bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white text-[10px] px-3 py-1 rounded-full font-bold tracking-wider uppercase shadow-lg shadow-fuchsia-500/30">
                                            Co-Pilot Coming Soon
                                        </span>
                                    </div>

                                    <p className={`text-lg mb-6 leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                        A breakthrough in safety psychology. Clone the voice of your spouse or child to deliver critical alerts. Studies show hearing a loved one reduces stress and improves reaction time in emergencies.
                                    </p>

                                    <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                                        <Button
                                            className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white border-none shadow-lg shadow-fuchsia-600/30"
                                            size="lg"
                                        >
                                            Join Exclusive Waitlist
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="lg"
                                            className={`${isDarkMode ? 'border-slate-700 text-gray-300 hover:bg-slate-800' : 'border-gray-200 text-gray-700'}`}
                                        >
                                            Learn More
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Audio Settings Grid */}
                <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">

                    {/* Wake Word Config */}
                    <div className={`rounded-3xl p-6 ${isDarkMode ? 'bg-slate-900' : 'bg-white'} border ${isDarkMode ? 'border-slate-800' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                                    <Mic className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                </div>
                                <div>
                                    <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Wake Word</h4>
                                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Activation phrase</p>
                                </div>
                            </div>
                            <Switch checked={wakeWordEnabled} onCheckedChange={setWakeWordEnabled} />
                        </div>

                        <div className={`p-4 rounded-xl border-2 border-dashed ${isDarkMode ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-gray-50'}`}>
                            <div className="flex items-center justify-between">
                                <span className={`font-mono text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                    "Hey OilWell"
                                </span>
                                {wakeWordEnabled && (
                                    <div className="flex gap-1">
                                        {[1, 2, 3].map(i => (
                                            <motion.div
                                                key={i}
                                                animate={{ height: [10, 20, 10] }}
                                                transition={{ repeat: Infinity, duration: 1, delay: i * 0.1 }}
                                                className="w-1 bg-green-500 rounded-full"
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Advanced Sliders */}
                    <div className={`rounded-3xl p-6 ${isDarkMode ? 'bg-slate-900' : 'bg-white'} border ${isDarkMode ? 'border-slate-800' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <Settings2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Voice Tuning</h4>
                                <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Customize speech</p>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="space-y-3">
                                <div className="flex justify-between text-sm">
                                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Speed</span>
                                    <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{voiceSpeed}x</span>
                                </div>
                                <Slider
                                    value={voiceSpeed}
                                    min={0.5}
                                    max={2.0}
                                    step={0.1}
                                    onValueChange={setVoiceSpeed}
                                    className={`py-2 ${isDarkMode ? '[&>[data-slot=slider-track]]:bg-slate-700 [&>[data-slot=slider-range]]:bg-blue-500' : ''}`}
                                />
                            </div>

                            <div className="space-y-3">
                                <div className="flex justify-between text-sm">
                                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Pitch</span>
                                    <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{voicePitch}</span>
                                </div>
                                <Slider
                                    value={voicePitch}
                                    min={0.5}
                                    max={1.5}
                                    step={0.1}
                                    onValueChange={setVoicePitch}
                                    className={`py-2 ${isDarkMode ? '[&>[data-slot=slider-track]]:bg-slate-700 [&>[data-slot=slider-range]]:bg-blue-500' : ''}`}
                                />
                            </div>
                        </div>
                    </div>

                </motion.div>

            </motion.div>
        </div>
    );
}
